import React, { useState, useRef, useEffect } from "react";
import { X, ChevronLeft, ChevronRight, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../../context/CartContext";

const formatPrice = (amount, currency) => {
  if (amount == null) return "";
  const symbols = { USD: "$", GBP: "£", EUR: "€" };
  const sym = symbols[currency] || "";
  return `${sym}${Number(amount).toFixed(2)}${sym ? "" : " " + (currency || "")}`;
};

export const ProductQuickView = ({ product, onClose }) => {
  const { addItem, loading } = useCart();
  const [imgIndex, setImgIndex] = useState(0);
  const [selected, setSelected] = useState(null);
  const [sizeError, setSizeError] = useState(false);
  const touchStartX = useRef(null);

  const images = product.images?.length ? product.images : [product.image].filter(Boolean);
  const variants = product.variants || [];

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") next();
      if (e.key === "ArrowLeft") prev();
    };
    document.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, []);

  const next = () => setImgIndex((i) => (images.length ? (i + 1) % images.length : 0));
  const prev = () => setImgIndex((i) => (images.length ? (i - 1 + images.length) % images.length : 0));

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd = (e) => {
    if (touchStartX.current == null) return;
    const delta = e.changedTouches[0].clientX - touchStartX.current;
    if (delta > 50) prev();
    else if (delta < -50) next();
    touchStartX.current = null;
  };

  const price = selected?.price != null
    ? formatPrice(selected.price, selected.currency)
    : formatPrice(product.price, product.currency);

  const handleAdd = async () => {
    if (!selected) {
      setSizeError(true);
      toast.error("Please select a size first");
      return;
    }
    await addItem(selected.id, 1);
    onClose();
  };

  return (
    <div
      data-testid="product-quickview"
      className="fixed inset-0 z-[100] flex items-center justify-center p-4 md:p-8"
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-matte/80 backdrop-blur-sm" />
      <div
        className="relative bg-cream text-matte w-full max-w-4xl max-h-[90vh] overflow-y-auto grid grid-cols-1 md:grid-cols-2 shadow-2xl"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="quickview-close"
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-matte/90 text-cream flex items-center justify-center hover:bg-matte transition-colors"
          aria-label="Close"
        >
          <X size={18} />
        </button>

        {/* Image gallery */}
        <div
          className="relative bg-white aspect-[4/5] md:aspect-auto md:h-full overflow-hidden select-none"
          onTouchStart={onTouchStart}
          onTouchEnd={onTouchEnd}
        >
          {images.length > 0 && (
            <img
              data-testid="quickview-image"
              src={images[imgIndex]}
              alt={`${product.title} view ${imgIndex + 1}`}
              className="w-full h-full object-cover"
              draggable={false}
            />
          )}
          {images.length > 1 && (
            <>
              <button
                data-testid="quickview-prev"
                onClick={prev}
                className="absolute left-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream/90 text-matte flex items-center justify-center hover:bg-cream transition-colors"
                aria-label="Previous image"
              >
                <ChevronLeft size={20} />
              </button>
              <button
                data-testid="quickview-next"
                onClick={next}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-10 h-10 rounded-full bg-cream/90 text-matte flex items-center justify-center hover:bg-cream transition-colors"
                aria-label="Next image"
              >
                <ChevronRight size={20} />
              </button>
              <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                {images.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setImgIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${i === imgIndex ? "w-6 bg-matte" : "w-1.5 bg-matte/40"}`}
                    aria-label={`Go to image ${i + 1}`}
                  />
                ))}
              </div>
            </>
          )}
        </div>

        {/* Details */}
        <div className="p-8 md:p-10 flex flex-col">
          <h3 className="heading text-3xl md:text-4xl text-matte">{product.title}</h3>
          <p data-testid="quickview-price" className="text-matte/70 text-lg mt-2 mb-6">{price}</p>

          {variants.length > 0 && (
            <div className="mb-6">
              <p className={`uppercase tracking-[0.2em] text-[0.7rem] font-bold mb-3 transition-colors ${sizeError ? "text-red-600" : "text-matte/50"}`}>
                {sizeError ? "Please select a size" : "Select Size"}
              </p>
              <div className="flex flex-wrap gap-2">
                {variants.map((v) => {
                  const isSel = selected?.id === v.id;
                  return (
                    <button
                      key={v.id}
                      data-testid={`size-${v.label}`}
                      disabled={!v.available}
                      onClick={() => { setSelected(v); setSizeError(false); }}
                      className={`min-w-[3rem] px-4 py-3 uppercase text-sm font-bold border transition-colors ${
                        isSel
                          ? "bg-matte text-cream border-matte"
                          : `bg-transparent text-matte hover:border-matte ${sizeError ? "border-red-500" : "border-matte/30"}`
                      } ${!v.available ? "opacity-30 line-through cursor-not-allowed" : ""}`}
                    >
                      {v.label}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          <button
            data-testid="quickview-add-to-cart"
            disabled={loading}
            onClick={handleAdd}
            className="mt-auto inline-flex items-center justify-center gap-2 bg-matte text-cream px-6 py-4 uppercase tracking-[0.2em] text-xs font-bold hover:bg-charcoal transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
          >
            <ShoppingBag size={16} /> Add To Bag
          </button>
        </div>
      </div>
    </div>
  );
};
